/* eslint-disable camelcase */
import { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import { results, PlainTable, HorizontalTable, CopyButton, DataPreviewTooltip } from '@mdc/ui';
import { Authenticator } from '@mdc/services';
import SummaryData from './SummaryData';
import ExpandableRow from '../../dynamic-analysis/ExpandableRow';

const { SandboxScore, PlainTablePlaceholder, SandboxTabs, CodeSnippet } = results;

import './Summary.scss';

const EXPANDABLE_ROW_COLOR = [
    { minScore: 0, maxScore: 5, color: 'gray' },
    { minScore: 6, maxScore: 7, color: 'warning' },
    { minScore: 8, maxScore: 10, color: 'danger' }
];

const Summary = ({ resultsLoading, scanning, fileImage, sandboxResults, id, action, opswatSandbox }) => {
    const { t, ready, i18n } = useTranslation();
    const processes = sandboxResults?.data?.summary?.processes;
    const dnsRequests = sandboxResults?.data?.summary?.network?.requests;
    const signatures = sandboxResults?.data?.summary?.signatures;
    const mitreData = sandboxResults?.data?.mitreData;
    const sandboxId = sandboxResults?.data?.sandbox_id;

    const apiKey = Authenticator.apikey;

    const processRawData = (data, meta) => meta.filter((row) => Object.hasOwnProperty.call(data, row.accessor) && (data[row.accessor]))
        .map((row) => {
            let value = row.parser ? row.parser(data[row.accessor]) : data[row.accessor];
            value = Array.isArray(value) ? value.join(', ') : value;

            return { field: row.header, data: value };
        });

    const downloadRawData = (sandboxResults) => {
        if (!opswatSandbox) {
            return;
        }

        const fileName = sandboxResults?.data?.data_id || sandboxResults?.data?.sha1 || sandboxResults?.data?.md5;
        const jsonData = JSON.stringify(sandboxResults?.data);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const href = URL.createObjectURL(blob);

        return {
            href, fileName
        };
    };

    const dnsRequestsData = dnsRequests && [].concat(...dnsRequests?.map((request) => {
        return { domains: request?.dns_request?.domains, flow: request?.flow };
    }));

    const aboutDynamicAnalysisDom = useMemo(() => <>
        {/*eslint-disable-next-line no-useless-escape*/}
        <p className='aboutDynamicAnalysis' dangerouslySetInnerHTML={{ __html: t('Read more about dynamic analysis on our <a href=\"https://onlinehelp.opswat.com/mdcloud/4._Dynamic_analysis.html\" target=\"_blank\">Documentation</a> page') }}/>
    </>, [t]);

    const generateTableBanner = (text) => {
        return <div className='labelBanner'>
            <span dangerouslySetInnerHTML={{ __html: text }}/>
        </div>;
    };

    const expandableRowDom = (process) => {
        const cmd = process.cmd;
        const image = process.image;
        const started = process.started;
        const terminated = process.terminated;
        const pid = process.pid;
        const procid_parent = process.procid_parent;
        const ppid = process.ppid;

        const mainProcessData = {
            cmd: cmd,
            pid: pid,
            ppid: ppid,
            image: image,
            procid: pid,
            procid_parent: procid_parent,
            started: started,
            terminated: terminated,
        };

        return (
            <Fragment>
                <HorizontalTable data={processRawData(mainProcessData, SummaryData(t).processes.main)}/>
            </Fragment>
        );
    };

    const processesMappedByID = useMemo(() => {
        if (!processes) {
            return;
        }

        return processes.reduce((acc, element, index) => {
            acc[element.procid] = index;
            return acc;
        }, {});
    }, [processes]);

    const createTreeHierarchy = (processes) => {
        if (!processes) {
            return;
        }

        let root;
        const minProcessStarted = Math.min.apply(Math, processes.map((process) => process?.started));
        const rootProcess = processes.filter((process) => process.started === minProcessStarted)[0];

        processes.forEach((process) => {
            if (rootProcess.procid === process.procid) {
                root = process;
                return;
            }

            const parentEl = processes[processesMappedByID[process.procid_parent]];
            if (parentEl) {
                parentEl.children = [...(parentEl.children || []), process];
            }
        });

        return [root];
    };

    const generateProcessDom = (process) => {
        const generateExpandableRowDom = (process) => {
            const processId = process.pid;

            return <ExpandableRow
                key={processId}
                title={t('Process #{{pid}}', { pid: processId })}
                tableDom={expandableRowDom(process)}
                titleSize='md'
            />;
        };

        const childProcesses = (process) => (process.children || []).map((proc, index) => {
            return <li key={index}>
                {generateExpandableRowDom(proc)}
                {proc.children && <ul>{childProcesses(proc)}</ul>}
            </li>;
        });

        return <ul className='processes' key={process?.pid}>
            <li className='rootProcess'>
                {generateExpandableRowDom(process)}
                <ul className='childProcesses'>
                    {childProcesses(process)}
                </ul>
            </li>
        </ul>;
    };

    const processDom = useMemo(() => {
        if (opswatSandbox) {
            return;
        }

        if (!processes) {
            return <PlainTablePlaceholder numberOfColumns={2}/>;
        }

        const hierarchyProcesses = createTreeHierarchy(processes);
        return hierarchyProcesses.map((process) => generateProcessDom(process));
    }, [processes, t, opswatSandbox]);

    const processIndicators = (indicators) => indicators?.map((indicator, index) => {
        const ioc = indicator?.ioc || null;

        if (typeof ioc !== 'string') {
            return indicator;
        }

        const iocProcessed = ioc ?
            <div className='d-block d-md-flex align-items-center justify-content-between' key={index}>
                <DataPreviewTooltip content={indicator?.ioc} customClassName='ioc'/>
                <CopyButton icon={'clone'} data={ioc}/>
            </div> :
            <DataPreviewTooltip content={indicator?.ioc} customClassName='ioc' key={index}/>;

        indicator.ioc = iocProcessed;
        return indicator;
    });

    const mitreDom = useMemo(() => {
        if (!mitreData || !mitreData?.length || opswatSandbox) {
            return null;
        }

        return mitreData?.map((data, key) => {
            const { id, name } = data;
            return <li key={key} className='mitreData'>
                <span>{id}</span>
                {name}
            </li>;
        });

    }, [mitreData, opswatSandbox]);

    const signaturesDom = useMemo(() => {
        if (opswatSandbox) {
            return null;
        }

        if (!signatures || resultsLoading || scanning) {
            return <PlainTablePlaceholder numberOfColumns={2}/>;
        }

        if (!signatures.length) {
            return null;
        }

        const signatureExpandableRow = (signature) => {
            const { indicators, tags, ttp, name } = signature;
            const mainSignatureData = {
                name: name,
                ttp: ttp,
                tags: tags
            };

            return (
                <Fragment>
                    <HorizontalTable data={processRawData(mainSignatureData, SummaryData(t).signatures.main)}/>

                    {indicators && <>
                        <h6>{t('Indicators')}</h6>
                        <PlainTable
                            columnsData={SummaryData(t).signatures.indicators.main}
                            data={processIndicators(indicators)}
                        />
                    </>}
                </Fragment>
            );
        };

        // TO-DO: Remove `processedSignatures` variable when all hashes have `score` property
        const processedSignatures = signatures.map((signature) => {
            const score = signature?.score || 0;
            signature.score = score;

            return signature;
        });

        const signatureSortedByScore = processedSignatures.sort((signature1, signature2) => {
            if (signature1?.score < signature2?.score) {
                return 1;
            }

            return signature1?.score > signature2?.score ? -1 : 0;
        });

        return signatureSortedByScore.map((signature) => {
            const signatureName = signature.name;
            const expandableRowColor = EXPANDABLE_ROW_COLOR.filter((entry) => signature?.score >= entry.minScore && signature?.score <= entry.maxScore)[0] || { color: 'gray' };

            return <ExpandableRow
                key={signatureName}
                backgroundColor={expandableRowColor?.color}
                title={t(signatureName)}
                tableDom={signatureExpandableRow(signature)}
                titleSize='sm'
                extraHeaderFields={signature?.tags}
            />;

        });

    }, [signatures, resultsLoading, scanning, t, opswatSandbox]);

    const downloadRawDataDom = useMemo(() => {
        if (!sandboxResults || !opswatSandbox) {
            return;
        }

        const { href, fileName } = downloadRawData(sandboxResults);
        const linkDom = <a href={href} download={`${fileName}.json`} className='downloadSanitizedButton pl-0 btn btn-link'>
            <i className='icon icon-down'/>
            {t('Download RAW data')}
        </a>;

        return linkDom;
    }, [sandboxResults, opswatSandbox, t]);

    const dynamicAnalysisDom = useMemo(() => {
        if (!sandboxResults || !sandboxResults?.data || resultsLoading || scanning) {
            return <>
                <HorizontalTable placeHolderRows={8}/>
                <PlainTablePlaceholder numberOfColumns={2}/>
            </>;
        }
        // eslint-disable-next-line camelcase
        const optionalDownloadLink = {
            ...(opswatSandbox && { downloadRawData: downloadRawDataDom })
        };

        const generalData = Object.assign({}, sandboxResults.data.scan_results, sandboxResults.data, optionalDownloadLink);
        const lang = i18n?.language?.split('-')[0] || 'en';

        return <>
            <div className='mainDynamicAnalysis'>
                {/* eslint-disable-next-line camelcase */}
                <HorizontalTable data={processRawData(generalData, SummaryData(t, lang).general)}/>
            </div>

            {signaturesDom && <ExpandableRow
                title={t('Signatures')}
                className='signatures'
                tableDom={signaturesDom}
                titleSize='md'
            />}

            {mitreDom && <ExpandableRow
                title={t('Mitre')}
                className='mitre'
                tableDom={<ul>{mitreDom}</ul>}
                titleSize='md'
            />}

            {processDom}

            {(sandboxResults.data?.summary?.network?.flows || sandboxResults.data?.summary?.network?.requests) && !opswatSandbox && <ExpandableRow
                title={t('Network')}
                className='network'
                tableDom={<>
                    {sandboxResults.data.summary.network?.flows && <PlainTable columnsData={SummaryData(t).network.flows} data={sandboxResults.data.summary.network.flows} tableBanner={generateTableBanner(t('Flows'))}/>}
                    {sandboxResults.data.summary.network?.requests && <PlainTable columnsData={SummaryData(t).network.requests} data={dnsRequestsData} tableBanner={generateTableBanner(t('Requests'))}/>}
                </>}
                titleSize='md'
            />}
        </>;
    }, [sandboxResults, resultsLoading, scanning, mitreData, t, opswatSandbox, downloadRawDataDom]);

    const codeSnippetDOM = useMemo(() => {
        return <CodeSnippet
            apiKey={apiKey}
            id={action === 'hash' ? `${id}/sandbox` : sandboxId}
            type={action === 'hash' ? 'hash' : 'sandbox'}
        />;
    }, [apiKey, id, action, sandboxId]);

    if (!ready) {
        return null;
    }

    return <SandboxTabs className='dynamicAnalysis sandboxSummary'>
        <Row>
            <Col lg={3} md={3} xs={12}>
                <SandboxScore
                    resultsLoading={resultsLoading}
                    scanning={scanning}
                    fileImage={fileImage}
                    sandboxResults={sandboxResults}
                    category={t('Summary')}
                />
                {aboutDynamicAnalysisDom}
            </Col>

            <Col lg={9} md={9} xs={12} className='SummaryDataWrapper'>
                {dynamicAnalysisDom}
            </Col>
        </Row>
        <Row className='mt-5'>
            <Col>
                {codeSnippetDOM}
            </Col>
        </Row>
    </SandboxTabs>;
};

Summary.propTypes = {
    resultsLoading: PropTypes.bool,
    scanning: PropTypes.string,
    fileImage: PropTypes.object.isRequired,
    sandboxResults: PropTypes.object,
    id: PropTypes.string,
    action: PropTypes.string,
    opswatSandbox: PropTypes.bool
};
export default Summary;
